import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { LogOut, Menu, Moon, Sun, User as UserIcon, ShieldAlert, X, Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { logout } from '@/lib/auth';
import { callAdminAssumeOrg } from '@/lib/functions';
import { useT } from '@/lib/i18n';
import { Logo } from './Logo';
import { LangSwitcher } from './LangSwitcher';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

export function Header({ onMenuClick, scrolled = false }: { onMenuClick?: () => void; scrolled?: boolean }) {
  const { user, profile, isGlobalAdmin, claims, effectiveOrgId, refresh } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  const [exitingAssume, setExitingAssume] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
  }, [dark]);

  const exitAssume = async () => {
    setExitingAssume(true);
    try {
      await callAdminAssumeOrg({ orgId: null });
      await refresh();
      toast.success('OK');
      nav('/admin');
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setExitingAssume(false);
    }
  };

  const orgBadge = isGlobalAdmin && claims.assumedOrgId
    ? (
      <button
        onClick={exitAssume}
        disabled={exitingAssume}
        className="group inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-[0.7rem] font-semibold text-amber-800 transition hover:border-amber-400 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-200 dark:hover:bg-amber-900/60"
        title={t('header.assumed_exit')}
      >
        <ShieldAlert className="h-3 w-3" />
        <span className="hidden sm:inline">{t('header.assumed_as')}</span>
        <code className="text-[0.65rem]">{claims.assumedOrgId.slice(0, 12)}</code>
        {exitingAssume
          ? <Loader2 className="h-3 w-3 animate-spin" />
          : <X className="h-3 w-3 opacity-60 group-hover:opacity-100" />}
      </button>
    )
    : effectiveOrgId
      ? <Badge variant="info" className="text-[0.65rem] sm:text-xs"><code className="text-[0.65rem]">{effectiveOrgId.slice(0, 12)}</code></Badge>
      : null;

  return (
    <header className={[
      'sticky top-0 z-30 flex items-center gap-2 px-3 py-3 sm:px-6 sm:gap-3 transition-all duration-300',
      // Light mode
      scrolled
        ? 'border-b border-slate-200 bg-white/80 backdrop-blur'
        : 'border-b border-transparent bg-white/10',
      // Dark mode overrides — subtle bg at rest so icons stay readable
      scrolled
        ? 'dark:border-border/30 dark:bg-background/80 dark:backdrop-blur-lg'
        : 'dark:border-transparent dark:bg-card/10',
    ].join(' ')}>
      {/* Mobile burger */}
      {onMenuClick && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onMenuClick}
          className="lg:hidden text-ink-700 dark:text-foreground/70"
          aria-label={t('header.menu')}
        >
          <Menu className="h-5 w-5" />
        </Button>
      )}

      {/* Mobile icon */}
      <div className="flex items-center lg:hidden">
        <Logo variant="icon" className="h-7 w-7" />
      </div>

      <div className="hidden flex-1 lg:flex" />

      {orgBadge}

      <div className="ml-auto flex items-center gap-1 sm:gap-2">
        <LangSwitcher />
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setDark(!dark)}
          aria-label={t('header.theme.toggle')}
          title={t('header.theme.toggle')}
        >
          {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>
        <div className="hidden items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 dark:border-border/50 dark:bg-card/60 md:flex">
          <div className="grid h-7 w-7 place-items-center rounded-full bg-brand-100 text-brand-700 dark:bg-primary/10 dark:text-primary">
            <UserIcon className="h-3.5 w-3.5" />
          </div>
          <div className="text-xs leading-tight">
            <div className="max-w-[160px] truncate font-medium text-ink-900 dark:text-foreground">{profile?.displayName ?? user?.email}</div>
            <div className="max-w-[160px] truncate text-ink-500 dark:text-muted-foreground">{user?.email}</div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={logout}
          aria-label={t('header.signout')}
          title={t('header.signout')}
        >
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </header>
  );
}
